import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
    Accordion,
    AccordionDetails,
    AccordionProps,
    AccordionSummary,
    AccordionSummaryProps,
    AccordionDetailsProps,
    SxProps,
    Theme,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { SystemStyleObject } from "@mui/system"
import { ReactElement } from "react"

type SxElement =
    | boolean
    | SystemStyleObject<Theme>
    | ((theme: Theme) => SystemStyleObject<Theme>)

function normaliseSx(sx: SxProps<Theme> | undefined): SxElement[] {
    if (!sx) return []
    if (Array.isArray(sx)) return sx as SxElement[]
    return [sx as SxElement]
}

/**
 * Styled accordion used across threshold management views.
 * Provides consistent rounded-border, alpha-tinted styling
 * for change-request and draft-change accordions.
 */
function ChangeAccordion({
    children,
    sx,
    ...rest
}: AccordionProps): ReactElement {
    return (
        <Accordion
            disableGutters
            sx={[
                (theme) => ({
                    border: `1px solid ${
                        theme.palette.mode === "dark"
                            ? alpha(theme.palette.primary.main, 0.2)
                            : theme.palette.divider
                    }`,
                    borderRadius: "12px !important",
                    overflow: "hidden",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { margin: 0 },
                }),
                ...normaliseSx(sx),
            ]}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
        >
            {children}
        </Accordion>
    )
}

function ChangeAccordionSummary({
    children,
    sx,
    ...rest
}: AccordionSummaryProps): ReactElement {
    return (
        <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={[
                (theme) => ({
                    backgroundColor:
                        theme.palette.mode === "dark"
                            ? alpha(theme.palette.primary.main, 0.06)
                            : alpha(theme.palette.primary.main, 0.04),
                    "&:hover": {
                        backgroundColor:
                            theme.palette.mode === "dark"
                                ? alpha(theme.palette.primary.main, 0.1)
                                : alpha(theme.palette.primary.main, 0.08),
                    },
                    minHeight: 56,
                    px: { xs: 1.5, sm: 2 },
                }),
                ...normaliseSx(sx),
            ]}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
        >
            {children}
        </AccordionSummary>
    )
}

function ChangeAccordionDetails({
    children,
    sx,
    ...rest
}: AccordionDetailsProps): ReactElement {
    return (
        <AccordionDetails
            sx={[
                (theme) => ({
                    backgroundColor: theme.palette.background.paper,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    p: { xs: 1.5, sm: 2 },
                }),
                ...normaliseSx(sx),
            ]}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
        >
            {children}
        </AccordionDetails>
    )
}

export { ChangeAccordion, ChangeAccordionSummary, ChangeAccordionDetails }
